<script setup lang="ts">
const { forgotPassword, setRecoveryPasswordFlow } = useAuthStore();
const { recoveryPasswordFlow } = storeToRefs(useAuthStore());
const { $containment, $kyc } = useNuxtApp();
const { t } = useTexts();
const { openResetPassword, openLogin } = useAuth();

interface RecoveryOption {
  id: Extract<Containment.PendencyName, 'email' | 'phone' | 'kyc'>;
  label: string;
  description: string;
  icon: string;
}

const recoveryOptions = computed<RecoveryOption[]>(() => {
  const options: RecoveryOption[] = [];
  const recoveryFlow = recoveryPasswordFlow.value;

  if (
    recoveryFlow.active_options?.feature_email_password_recovery &&
    recoveryFlow.email?.length
  ) {
    options.push({
      id: 'email',
      label: t('general.email'),
      description: t('auth.recover_password.methods.email.description', {
        email: recoveryFlow.email,
      }),
      icon: 'email_message',
    });
  }

  if (
    recoveryFlow.active_options?.feature_phone_password_recovery &&
    recoveryFlow.final_phone?.length
  ) {
    options.push({
      id: 'phone',
      label: t('general.sms'),
      description: t('auth.recover_password.methods.phone.description', {
        phone: recoveryFlow.final_phone,
      }),
      icon: 'sms',
    });
  }

  if (
    recoveryFlow.active_options?.feature_kyc_password_recovery &&
    recoveryFlow.kyc
  ) {
    options.push({
      id: 'kyc',
      label: t('general.facial_recognition'),
      description: t('auth.recover_password.methods.kyc.description'),
      icon: 'user/scan_face',
    });
  }

  return options;
});

function isRecoveryOptionValid(option: RecoveryOption): boolean {
  if (!option.id) {
    return false;
  }

  const currentOptions = (recoveryOptions.value || []).map(
    (option) => option.id,
  );

  return currentOptions.includes(option.id);
}

async function selectOptions(option: RecoveryOption): Promise<void> {
  if (!isRecoveryOptionValid(option)) {
    return;
  }

  if (option.id === 'kyc') {
    $kyc.setSource('password_recovery');
  }

  await $containment.handle([{ name: option.id, frequent: true }], () => {
    openResetPassword();
  });
}

const onClickHelpButton = (): void => {
  navigateTo(localeLink('/help'), {
    open: {
      target: '_blank',
      windowFeatures: {
        width: 860,
        height: 760,
      },
    },
  });
};

watchEffect(() => {
  if (recoveryOptions.value.length === 1) {
    const option = recoveryOptions.value[0];

    if (option) {
      selectOptions(option);
    }
  }
});

onMounted(() => {
  if (
    $kyc.current.source === 'password_recovery_attempt_limit' &&
    recoveryPasswordFlow.value?.token
  ) {
    forgotPassword({
      recovery_token: recoveryPasswordFlow.value?.token,
    }).then((response) => {
      if (response.status.toString().startsWith('20')) {
        setRecoveryPasswordFlow(response.data);
      }
    });
  }
});
</script>

<template>
  <ClientOnly>
    <div class="w-full">
      <div :class="$style.recoverContent">
        <h2> {{ $t('auth.recover_password.methods.choose_method') }}</h2>

        <div class="space-y-4">
          <button
            v-for="option in recoveryOptions"
            :key="option.id"
            :class="$style.optionItem"
            @click="selectOptions(option)"
          >
            <div class="flex items-start gap-3 text-left">
              <NuxtIcon
                :class="$style.itemIcon"
                :name="option.icon"
              />
              <div>
                <p :class="$style.title">{{ option.label }}</p>
                <p
                  :class="$style.description"
                  v-html="option.description"
                />
              </div>
            </div>
            <NuxtIcon
              class="text-texts/50"
              name="arrows/angle_right"
            />
          </button>
        </div>

        <div :class="$style.footer">
          <div class="text-auth-texts text-xs">
            {{ $t('auth.recover_password.methods.cant_recover') }}
            <button
              class="text-auth-links"
              @click.prevent="onClickHelpButton"
            >
              {{ $t('auth.recover_password.methods.my_password') }}
            </button>
          </div>

          <div class="text-auth-texts text-xs">
            {{ $t('auth.recover_password.methods.back_to') }}
            <button
              class="text-auth-links"
              @click.prevent="openLogin()"
            >
              {{ $t('auth.login.title') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.recoverContent {
  @apply w-full max-w-sm mx-auto rounded-2xl space-y-8 py-4;

  h2 {
    @apply text-center text-lg font-semibold text-texts;
  }
}

.optionItem {
  @apply w-full flex items-center justify-between rounded-lg px-2 py-3 hover:bg-[#2A2A2A] transition;

  .title {
    @apply text-sm font-semibold text-texts;
  }
  .description {
    @apply text-xs font-normal text-texts;

    span {
      @apply font-semibold;
    }
  }

  .itemIcon {
    @apply text-4xl text-primary;
  }
}

.footer {
  @apply flex flex-col items-center pt-4 border-solid border-t border-texts/40 text-center space-y-2 text-sm;
}
</style>
